import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import AIReadinessAssessment from './components/AIReadinessAssessment';
import LandingIframe from './components/LandingIframe';
import AlertModal from './components/AlertModal';
import apiService from './services/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAIReadiness, setShowAIReadiness] = useState(false);
  const [showGreeting, setShowGreeting] = useState(true);
  const [startError, setStartError] = useState('');
  const [alertModal, setAlertModal] = useState({ show: false, type: 'alert', title: '', message: '', onConfirm: null });
  const historyInitialized = useRef(false);

  // Initialize browser history and handle back/forward navigation
  useEffect(() => {
    // Initialize history state on mount
    if (!historyInitialized.current) {
      window.history.replaceState({ page: 'landing' }, '', window.location.href);
      historyInitialized.current = true;
    }

    // Handle browser back/forward buttons
    const handlePopState = (event) => {
      const state = event.state || { page: 'landing' };
      const page = state.page;

      console.log('[APP] Browser navigation:', page);

      // Update React state based on history state
      switch (page) {
        case 'landing':
          setShowGreeting(true);
          setShowAIReadiness(false);
          break;
        case 'ai-readiness':
          setShowGreeting(false);
          setShowAIReadiness(true);
          break;
        default:
          setShowGreeting(true);
          setShowAIReadiness(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const startAssessment = async (userData) => {
    try {
      setLoading(true);
      setStartError('');
      
      // Always use AIReadiness assessment type
      const userDataWithType = { ...userData, assessment_type: 'AIReadiness' };

      // Set user data
      setUser(userDataWithType);
      
      // Store user data in localStorage for AI Readiness Assessment
      if (userDataWithType) {
        localStorage.setItem('aiReadinessUser', JSON.stringify(userDataWithType));
      }

      // Create session with assessment_type
      console.log('[APP] Creating session with data:', {
        name: userDataWithType.name,
        email: userDataWithType.email,
        organization: userDataWithType.organization,
        assessment_type: userDataWithType.assessment_type
      });

      const sessionResponse = await apiService.createSession(userDataWithType);
      console.log('[APP] Session created successfully:', sessionResponse);

      setUser(sessionResponse.user);

      // Hide greeting page and show AI Readiness Assessment
      setShowGreeting(false);
      setShowAIReadiness(true);
      // Push to browser history
      window.history.pushState({ page: 'ai-readiness' }, '', window.location.href);
      
    } catch (error) {
      console.error('Error starting assessment:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // Show top-of-screen banner (keep landing page visible)
      let errorMsg = '';
      if (error.message) {
        errorMsg = error.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else {
        errorMsg = 'Backend is unavailable. Please try again.';
      }
      
      // Check for connection errors
      if (errorMsg.toLowerCase().includes('failed to fetch') || 
          errorMsg.toLowerCase().includes('cannot connect') ||
          error.name === 'TypeError') {
        errorMsg = `Cannot connect to backend server. Please ensure the backend is running on port 4000. (Error: ${errorMsg})`;
      }
      
      setStartError(errorMsg);
      // Auto-hide after 8s (longer for more detailed messages)
      setTimeout(() => setStartError(''), 8000);
    } finally {
      setLoading(false);
    }
  };

  // Show AI Readiness Assessment page
  if (showAIReadiness) {
    return <AIReadinessAssessment user={user} />;
  }

  // Show landing page (always AI Readiness)
  if (showGreeting) {
    return (
      <>
        {startError && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
              background: '#fee2e2',
              color: '#991b1b',
              borderBottom: '1px solid #fecaca',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 600
            }}
            role="alert"
          >
            <span>⚠️ {startError}</span>
            <button
              onClick={() => setStartError('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#991b1b',
                fontSize: '1rem',
                cursor: 'pointer',
                fontWeight: 700
              }}
              aria-label="Dismiss"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        <LandingIframe src="/landing-aireadiness.html" onStart={(userData) => startAssessment(userData)} />
      </>
    );
  }

  // Error Alert Modal
  return (
    <>
      <AlertModal
        show={alertModal.show}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
        confirmText="OK"
      />
    </>
  );
}

export default App;
